import { Field, useField, useFormikContext } from "formik";
import type React from "react";
import { useMemo, useState } from "react";

import { ExclamationCircleIcon } from "@heroicons/react/20/solid";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import EventCronParser from "event-cron-parser";
import NewLabelWrapper, { type LabelProps } from "./NewLabelWrapper";

interface Props {
  fieldName: string;
  disabled?: boolean;
  placeholder?: string;
  dataCy?: string;
  label?: LabelProps;
}

interface ValidationResult {
  isValid: boolean;
  error?: string;
  nextOccurrences?: Date[];
}

function validateAwsCron(expression: string): ValidationResult {
  if (!expression || expression.trim() === "") {
    return { isValid: false, error: "Cron expression is required" };
  }

  const parts = expression.trim().split(/\s+/);
  if (parts.length !== 6) {
    return {
      isValid: false,
      error: `Expected 6 fields, got ${parts.length}. Format: minute hour day-of-month month day-of-week year`,
    };
  }

  try {
    const parser = new EventCronParser(expression);
    parser.validate();
    const nextOccurrences: Date[] = [];

    // Get next 3 occurrences using event-cron-parser's next() method
    let current = new Date();
    for (let i = 0; i < 3; i++) {
      const nextDate = parser.next(current);
      if (nextDate) {
        nextOccurrences.push(nextDate);
        current = new Date(nextDate.getTime() + 1000);
      }
    }

    return { isValid: true, nextOccurrences };
  } catch (e) {
    return {
      isValid: false,
      error: e instanceof Error ? e.message : "Invalid cron expression",
    };
  }
}

const CRON_EXAMPLES = [
  { expression: "0 * * * ? *", description: "Every hour" },
  { expression: "*/15 * * * ? *", description: "Every 15 minutes" },
  { expression: "0 8 * * ? *", description: "Daily at 8:00 AM" },
  { expression: "0 0 1 * ? *", description: "First day of month at midnight" },
  { expression: "0 12 ? * MON-FRI *", description: "Weekdays at noon" },
];

export default function AwsCronInput(props: Props): React.ReactElement {
  const { disabled = false } = props;
  const [field, meta] = useField(props.fieldName);
  const { submitForm, setFieldValue } = useFormikContext();
  const [showHelper, setShowHelper] = useState(false);

  const validation = useMemo(() => {
    if (!field.value) return null;
    return validateAwsCron(field.value);
  }, [field.value]);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      event.preventDefault();
      submitForm();
    }
  };

  const handleExampleClick = (expression: string) => {
    setFieldValue(props.fieldName, expression);
  };

  const hasError =
    (meta.error && meta.touched) || (validation && !validation.isValid);

  return (
    <NewLabelWrapper {...props.label} for={props.fieldName}>
      <div className="relative">
        <Field
          id={props.fieldName}
          name={props.fieldName}
          aria-invalid={hasError ? "true" : "false"}
          className={clsx(
            "block w-full rounded-lg border-0 py-[7px] pr-10 sm:text-sm sm:leading-6 ring-1 font-mono",
            hasError
              ? "text-red-900 ring-red-300 placeholder:text-red-300 focus:ring-2 focus:ring-inset focus:ring-red-500"
              : "text-gray-900 shadow-sm ring-gray-300 placeholder:text-gray-400 focus:ring-brand-light-600",
          )}
          type="text"
          disabled={disabled}
          data-cy={props.dataCy || props.fieldName}
          placeholder={props.placeholder || "0 * * * ? *"}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          onClick={() => setShowHelper(!showHelper)}
          className="absolute inset-y-0 right-0 flex items-center pr-3"
        >
          <InformationCircleIcon
            aria-hidden="true"
            className={clsx(
              "h-5 w-5",
              showHelper
                ? "text-brand-light-600"
                : "text-gray-400 hover:text-gray-600",
            )}
          />
        </button>
      </div>

      {/* Validation error */}
      {hasError && (
        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
          <ExclamationCircleIcon className="h-4 w-4" />
          {meta.error || validation?.error}
        </p>
      )}

      {/* Validation success */}
      {validation?.isValid && !hasError && field.value && (
        <div className="mt-1 text-sm text-green-600">
          <p>Valid cron expression</p>
          {validation.nextOccurrences &&
            validation.nextOccurrences.length > 0 && (
              <div className="mt-1 text-xs text-gray-500">
                <span className="font-medium">Next runs:</span>
                <ul className="ml-2">
                  {validation.nextOccurrences.map((date, i) => (
                    <li key={date.getTime()}>{date.toLocaleString()}</li>
                  ))}
                </ul>
              </div>
            )}
        </div>
      )}

      {/* Helper panel */}
      {showHelper && (
        <div className="mt-2 p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm">
          <div className="mb-2">
            <span className="font-medium text-gray-700">
              AWS EventBridge Cron Format:
            </span>
            <code className="ml-2 px-2 py-0.5 bg-gray-200 rounded text-xs">
              minute hour day-of-month month day-of-week year
            </code>
          </div>

          <div className="mb-3 text-xs text-gray-500">
            <div className="grid grid-cols-6 gap-1 font-mono">
              <span>min</span>
              <span>hour</span>
              <span>dom</span>
              <span>month</span>
              <span>dow</span>
              <span>year</span>
            </div>
            <div className="grid grid-cols-6 gap-1 text-gray-400">
              <span>0-59</span>
              <span>0-23</span>
              <span>1-31</span>
              <span>1-12</span>
              <span>1-7</span>
              <span>*</span>
            </div>
          </div>

          <div className="space-y-1">
            <span className="font-medium text-gray-700 text-xs">
              Examples (click to use):
            </span>
            {CRON_EXAMPLES.map((example) => (
              <button
                key={example.expression}
                type="button"
                onClick={() => handleExampleClick(example.expression)}
                className="block w-full text-left px-2 py-1 rounded hover:bg-gray-200 transition-colors"
              >
                <code className="text-xs font-mono text-brand-light-700">
                  {example.expression}
                </code>
                <span className="ml-2 text-xs text-gray-500">
                  — {example.description}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-2 text-xs text-gray-500">
            <span className="font-medium">Special characters:</span> * (any), ?
            (no specific value), / (increments), - (range), , (list)
          </div>
        </div>
      )}
    </NewLabelWrapper>
  );
}
